import { Immutable } from "immer";
import React from "react";

import type { ReviewsFullBlock as Value } from "@reactivated";

import { ProductReviewsSSR } from "../../apps/reviews/ProductReviewsSSR";
import { ReviewsComponentTypes } from "../../apps/reviews/constants";

type IProps = Immutable<{
    value: Value;
    /**
     * Site-flavor overrides forwarded to ProductReviewsSSR. The legacy
     * `{% react_product_reviews %}` island mounted each site's
     * `ProductReviewsTemplate.tsx` re-export, which is where per-site branding
     * lives (e.g. sealy passes `brand="Sealy"`, cocoon passes
     * `starHasStroke` + CAROUSEL); each site's `ReviewsFullBlockTemplate.tsx`
     * re-export passes the same flavor here.
     */
    brand?: string;
    starHasStroke?: boolean;
    showWriteReviewButton?: boolean;
    reviewsComponentType?: ReviewsComponentTypes;
}>;

/**
 * Faithful port of the legacy server-rendered
 * ``tsicommon_cms/blocks/reviews-full-block.html`` (#33285/#33428):
 *
 * - `.reviews-full-block` wrapper + `#reviews` around the reviews island,
 *   exactly as the legacy template emitted them.
 * - `props.value.reviews` is the serialized output of the legacy
 *   `{% react_product_reviews value.products value %}` tag (brand id from the
 *   block's own products, bundle-deduped review aggregates, Reviews API SSR
 *   prefill), shape-identical to `templates.ProductReviewsTemplate`, so it
 *   spreads straight into `ProductReviewsSSR`. It is always present -- the
 *   legacy island mounted even with no products (brand id just serializes
 *   null), so there is no render gate here.
 * - `isCollapsedView={true}` matches every site's legacy
 *   `ProductReviewsTemplate.tsx` island.
 * - The CTA gates on `value.show_cta` ONLY (the legacy `{% if value.show_cta
 *   %}`; unlike the page-level sealy/toppers FullReviewsModule there is no
 *   `num_reviews > 0` gate). The href uses the Link snippet's resolved `url`
 *   (stearns/toppers/sealy port precedent): the legacy `{% if
 *   value.cta.link_page %}...{% else %}{{ value.cta }}{% endif %}` rendered
 *   `str(Link)` (a debug-ish "Link[...]: url" string) for non-page links,
 *   which was a latent template bug; `cta.url` resolves every link type
 *   correctly.
 */
export const ReviewsFullBlock = (props: IProps): React.JSX.Element => (
    <div className="reviews-full-block">
        <div id="reviews">
            <ProductReviewsSSR
                {...props.value.reviews}
                brand={props.brand}
                starHasStroke={props.starHasStroke}
                showWriteReviewButton={props.showWriteReviewButton}
                reviewsComponentType={props.reviewsComponentType}
                isCollapsedView={true}
            />
        </div>
        {props.value.show_cta && (
            <div className="pdp-reviews-full__cta">
                <a
                    className="button button--ghost pdp-reviews-full__cta-link"
                    href={props.value.cta?.url ?? ""}
                >
                    {props.value.cta_text}
                </a>
            </div>
        )}
    </div>
);
